import { React } from 'Imports';
import { IVideoEventServiceInjectedProps, VideoEventService } from '$State/VideoEventFreezerService';
import { useEffect } from 'react';
import { CommentList } from './CommentList';
import { faCirclePlus, faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import { Box, Button, TextField } from 'MaterialUIComponents';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { VideoEventCommentResponse } from '$Generated/api';
import { useState } from 'react';
import { ConfirmDialog } from '$Components/Shared/ConfirmDialog';
import { useAppSnackbar } from '$Utilities/hooks/useAppSnackbar';
import { AlertTypes } from '$Utilities/Enums/AlertTypes';
import { canEditByRole, RolesEnum } from '$Externals/VerifyRole';
import { isEncompass } from '$Utilities/integrationPlatform';

interface EventCommentsBaseProps {
    videoEventId: number;
}

type EventCommentsProps = EventCommentsBaseProps & IVideoEventServiceInjectedProps;

const styles = require('./EventComments.scss') as {
    container: string;
    buttonIconTextGeotab: string;
    buttonIconTextEncompass: string;
    buttonIcon: string;
    eventListContainer: string;
    commentFormContainer: string;
    commentCharCountGeotab: string;
    commentCharCountEncompass: string;
    commentFormActions: string;
    rightActions: string;
    disabled: string;
    complete: string;
    inputLabelGeotab: string;
    inputLabelEncompass: string;
};

const MAX_COMMENT_LENGTH = 400;

const _EventComments = (props: EventCommentsProps) => {
    const { videoEventAndDriverCommentsResult } = props.videoEvents.getState();
    const comments = videoEventAndDriverCommentsResult.data ?? [];

    const { showSnackbarMessage } = useAppSnackbar();

    const [isEditing, setIsEditing] = useState(false);
    const [editingComment, setEditingComment] = useState<VideoEventCommentResponse | null>(null);
    const [commentText, setCommentText] = useState('');
    const [showConfirmDeleteDialog, setShowConfirmDeleteDialog] = useState(false);

    const accessToComment = canEditByRole([RolesEnum.videoEventEdit, RolesEnum.videoRecallEdit, RolesEnum.encompassVideo]);

    useEffect(() => {
        if (props.videoEventId) {
            props.videoEvents.getVideoEventAndDriverComments(props.videoEventId);
        }
    }, [props.videoEventId]);

    const handleAddClick = () => {
        setEditingComment(null);
        setCommentText('');
        setIsEditing(true);
    };

    const handleEditClick = (comment: VideoEventCommentResponse) => {
        setEditingComment(comment);
        setCommentText(comment.comment ?? '');
        setIsEditing(true);
    };

    const handleCancel = () => {
        setIsEditing(false);
        setEditingComment(null);
        setCommentText('');
    };

    const handleSave = async () => {
        const body = { comment: commentText };
        if (editingComment) {
            await props.videoEvents
                .putVideoEventComments(props.videoEventId, editingComment.id!, body)
                .then(() => {
                    showSnackbarMessage({
                        message: 'Comment saved successfully.',
                        key: `comment-saved`,
                        alertType: AlertTypes.Success,
                        isEncompass: isEncompass,
                        showIcon: isEncompass,
                        useNewIcons: isEncompass,
                    });
                })
                .catch(() => {
                    showSnackbarMessage({
                        message: 'Comment unable to be saved.',
                        alertType: AlertTypes.Fail,
                        isEncompass: isEncompass,
                        showIcon: isEncompass,
                        useNewIcons: isEncompass,
                    });
                });
        } else {
            await props.videoEvents
                .postVideoEventComment(props.videoEventId, body)
                .then(() => {
                    showSnackbarMessage({
                        message: 'Comment saved successfully.',
                        key: `comment-saved`,
                        alertType: AlertTypes.Success,
                        isEncompass: isEncompass,
                        showIcon: isEncompass,
                        useNewIcons: isEncompass,
                    });
                })
                .catch(() => {
                    showSnackbarMessage({
                        message: 'Comment unable to be saved.',
                        alertType: AlertTypes.Fail,
                        isEncompass: isEncompass,
                        showIcon: isEncompass,
                        useNewIcons: isEncompass,
                    });
                });
        }
        await props.videoEvents.getVideoEventAndDriverComments(props.videoEventId);
        handleCancel();
    };

    const handleToggleConfirmDeleteDialog = () => {
        setShowConfirmDeleteDialog((prev) => !prev);
    };

    const handleDelete = async () => {
        if (editingComment) {
            await props.videoEvents
                .deleteVideoEventComments(props.videoEventId, editingComment.id!)
                .then(() => {
                    showSnackbarMessage({
                        message: 'Comment deleted successfully.',
                        key: `comment-deleted`,
                        alertType: AlertTypes.Success,
                        isEncompass: isEncompass,
                        showIcon: isEncompass,
                        useNewIcons: isEncompass,
                    });
                })
                .catch(() => {
                    showSnackbarMessage({
                        message: 'Comment unable to be deleted.',
                        alertType: AlertTypes.Fail,
                        isEncompass: isEncompass,
                        showIcon: isEncompass,
                        useNewIcons: isEncompass,
                    });
                });
            await props.videoEvents.getVideoEventAndDriverComments(props.videoEventId);
            handleToggleConfirmDeleteDialog();
            handleCancel();
        }
    };

    const trimmedText = commentText.trim();
    const originalText = (editingComment?.comment ?? '').trim();
    const saveButtonDisabled = !trimmedText || (editingComment !== null && trimmedText === originalText);
    return (
        <div>
            <Button
                variant="text"
                size="small"
                disabled={!accessToComment || isEditing}
                sx={{ color: isEncompass ? '#C94F0D' : '#0078D3' }}
                startIcon={<FontAwesomeIcon className={styles.buttonIcon} icon={faCirclePlus} />}
                onClick={handleAddClick}
                className={isEncompass ? styles.buttonIconTextEncompass : styles.buttonIconTextGeotab}
            >
                <span>Add Comment</span>
            </Button>

            {!isEditing && (
                <div className={styles.eventListContainer}>
                    <CommentList comments={comments} onEdit={handleEditClick} accessToComment={accessToComment} />
                </div>
            )}

            {isEditing && (
                <div className={styles.commentFormContainer}>
                    <span className={isEncompass ? styles.inputLabelEncompass : styles.inputLabelGeotab}>
                        {editingComment ? 'Edit Comment' : 'Enter New Comment'}
                    </span>
                    <TextField
                        multiline
                        rows={4}
                        variant="outlined"
                        fullWidth
                        placeholder="Start typing ..."
                        value={commentText}
                        inputProps={{ maxLength: MAX_COMMENT_LENGTH, style: { fontSize: isEncompass ? '12px' : '14px' } }}
                        sx={{
                            '& .MuiInputBase-input::placeholder': { fontSize: isEncompass ? '12px' : '14px' },

                            '& .Mui-focused .VP-MuiOutlinedInput-notchedOutline': {
                                borderColor: isEncompass ? '#005480 !important' : '#0078D3 !important',
                                borderWidth: '1px !important',
                            },
                        }}
                        onChange={(e) => setCommentText(e.target.value)}
                    />
                    <div className={isEncompass ? styles.commentCharCountEncompass : styles.commentCharCountGeotab}>
                        {commentText.length}/{MAX_COMMENT_LENGTH}
                    </div>
                    <div className={styles.commentFormActions}>
                        {editingComment && (
                            <Button
                                variant="text"
                                size="small"
                                startIcon={<FontAwesomeIcon icon={faTrashAlt} className={styles.buttonIcon} />}
                                onClick={handleToggleConfirmDeleteDialog}
                                sx={{ color: isEncompass ? '#C94F0D' : 'primary' }}
                                className={isEncompass ? styles.buttonIconTextEncompass : styles.buttonIconTextGeotab}
                            >
                                <span>Delete Comment</span>
                            </Button>
                        )}
                        <div className={styles.rightActions}>
                            <Button
                                variant={isEncompass ? 'outlined' : 'text'}
                                size="small"
                                onClick={handleCancel}
                                color="primary"
                                sx={{
                                    textTransform: 'capitalize',
                                    fontWeight: isEncompass ? '700' : '400',
                                    ...(isEncompass ? { borderColor: '#0D7AC5' } : {}),
                                }}
                            >
                                <Box sx={{ fontSize: isEncompass ? '14px' : '12px' }}>Cancel</Box>
                            </Button>

                            <Button
                                color="primary"
                                onClick={handleSave}
                                className={saveButtonDisabled ? styles.disabled : styles.complete}
                                disabled={saveButtonDisabled}
                            >
                                <span>Save</span>
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            <ConfirmDialog
                isOpen={showConfirmDeleteDialog}
                title={isEncompass ? 'Delete Comment' : 'Confirm Delete'}
                contentMessage="Are you sure you want to permanently delete this comment?"
                cancelButton={isEncompass ? 'No, Go Back' : 'Cancel'}
                acceptButton={isEncompass ? 'Yes, Delete' : 'Delete'}
                contentMessageMarginTop={isEncompass ? '0' : undefined}
                isAlert={isEncompass}
                showIcon={isEncompass}
                handleModalClosed={handleToggleConfirmDeleteDialog}
                handleSubmit={handleDelete}
                isButtonAlert={true}
            />
        </div>
    );
};

export const EventComments = VideoEventService.inject(_EventComments);
